/**
 * @fileoverview Frontend configuration.
 * @package PlayItNow
 * @module apps/front/config
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

/**
 * @summary API base URL.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

/**
 * @summary API prefix.
 */
export const API_PREFIX = '/api/v1'

/**
 * @summary Full API URL.
 */
export const API_URL = `${API_BASE_URL}${API_PREFIX}`

/**
 * @summary Check if running in development mode.
 */
export const isDevelopment = import.meta.env.DEV

/**
 * @summary Check if running in production mode.
 */
export const isProduction = import.meta.env.PROD

/**
 * @summary App name.
 */
export const AppName = 'PlayItNow'

/**
 * @summary Live dashboard polling interval in milliseconds.
 * @default 5000 (5 seconds)
 */
export const LIVE_POLLING_INTERVAL = Number(import.meta.env.VITE_LIVE_POLLING_INTERVAL) || 15000

/**
 * @summary SuperAdmin dashboard URL for role-based redirection.
 * @description When a superadmin logs in or lands on the main frontend, they are redirected here.
 * Configurable via VITE_ADMIN_DASHBOARD_URL (e.g. http://localhost:3004 in dev).
 */
export const ADMIN_DASHBOARD_URL =
  import.meta.env.VITE_ADMIN_DASHBOARD_URL || 'https://admin.playitnow.live'

/**
 * @summary Enable Rybbit analytics script.
 * @description Set VITE_ANALYTICS_ENABLED=true only in production build. Leave unset in preprod/dev.
 */
export const analyticsEnabled = import.meta.env.VITE_ANALYTICS_ENABLED === 'true'

/**
 * @summary Public app base URL for QR codes and share links.
 * @description Must be an absolute URL with protocol (https:// or http://) so that phones
 * recognize the QR content as a link. Set VITE_PUBLIC_APP_URL in preprod/prod (e.g. https://app.playitnow.live).
 * Falls back to window.location.origin when available.
 */
export const PUBLIC_APP_BASE_URL = import.meta.env.VITE_PUBLIC_APP_URL ?? ''

/**
 * @summary Build absolute event URL for QR code so phones recognize it as a link.
 * @description Encoded QR content must start with https:// or http:// for scanners to offer "Open link".
 * @param qrCodeOrSlug - Event QR code token (slug).
 * @returns Absolute URL (e.g. https://app.playitnow.live/event/abc123).
 */
export function getEventUrlForQR(qrCodeOrSlug: string): string {
  const base =
    (typeof window !== 'undefined' && window.location?.origin) ||
    PUBLIC_APP_BASE_URL ||
    'https://app.playitnow.live'
  const baseNormalized = base.replace(/\/$/, '')
  return `${baseNormalized}/event/${qrCodeOrSlug}`
}
